import { useRef, useState } from 'react';
import styled from 'styled-components';
import ModalPortal from 'components/Modal/ModalPortal';
import PostureModalLayout from 'components/Modal/PostureModal/PostureModalLayout';
import useAddDataInBarChart from 'hooks/useAddDataInBarChart';
import { chartColor, color } from 'asserts/styles/colors';
import { commonTooltipOptions } from 'consts/chartOptions';
import { CSSTransition } from 'react-transition-group';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

import { ReactComponent as IconPostureResult } from 'asserts/icons/Icon_Posture_Result.svg';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface PostureStatisticsChartAProps {
  chartData: PostureData;
}

const PostureStatisticsRatingChart = ({
  chartData
}: PostureStatisticsChartAProps) => {
  const nodeRef = useRef<HTMLDivElement>(null);

  const [isOPenModal, setIsOpenModal] = useState<boolean>(false);

  const chartType = 'musculoskeletalLevel';
  const labels = ['6단계', '5단계', '4단계', '3단계', '2단계', '1단계'];

  const title = '근골격 지수';
  const detail = '평균 5단계';

  const musculoskeletalLevel = chartData?.musculoskeletalLevel || [];

  const musculoSkeletalLevelData = [
    musculoskeletalLevel.levelsix,
    musculoskeletalLevel.levelfive,
    musculoskeletalLevel.levelfour,
    musculoskeletalLevel.levelthree,
    musculoskeletalLevel.leveltwo,
    musculoskeletalLevel.levelone
  ];

  const {
    graphRed,
    graphOrange,
    graphYellow,
    graphGreen,
    graphViolet,
    graphMobile
  } = chartColor;

  const colors = [
    graphViolet,
    graphRed,
    graphOrange,
    graphYellow,
    graphGreen,
    graphMobile
  ];

  const data = {
    labels: labels,
    datasets: [
      {
        label: '전   후',
        data: musculoSkeletalLevelData,
        backgroundColor: colors,
        barThickness: 24,
        borderRadius: 3
      }
    ]
  };

  const options = {
    layout: {
      padding: {
        bottom: 38
      }
    },
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: commonTooltipOptions
    },
    scales: {
      x: {
        ticks: {
          color: `${color.white}`,
          font: {
            family: 'Noto Sans KR'
          }
        }
      },
      y: {
        grid: {
          color: `${color.white005}`
        }
      }
    }
  };

  const handleClickOpenModal = () => {
    setIsOpenModal(true);
  };

  const handleClickCloseModal = () => {
    setIsOpenModal(false);
  };

  const addDataInBarChart = {
    id: 'addDataInBarChart',
    afterDatasetsDraw: useAddDataInBarChart()
  };

  return (
    <>
      <Wrapper onClick={handleClickOpenModal}>
        <TitleWrapper>
          <IconPostureResult />
          <TextBox>
            <PositionTitle>{title}</PositionTitle>
            <PositionDetail>{detail}</PositionDetail>
          </TextBox>
        </TitleWrapper>
        <ChartWrapper>
          <Bar data={data} options={options} plugins={[addDataInBarChart]} />
        </ChartWrapper>
      </Wrapper>
      <CSSTransition
        in={isOPenModal}
        classNames="modal"
        nodeRef={nodeRef}
        timeout={300}
        unmountOnExit
      >
        <ModalPortal>
          <PostureModalLayout
            colors={colors}
            labels={labels}
            titleText={title}
            detailText={detail}
            selectedChartType={chartType}
            onClickModal={handleClickCloseModal}
          />
        </ModalPortal>
      </CSSTransition>
    </>
  );
};

export default PostureStatisticsRatingChart;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 272px;
  margin-bottom: 16px;
  background: ${color.white005};
  border-radius: 16px;
  cursor: pointer;
`;

const TitleWrapper = styled.div`
  display: flex;
  padding: 12px;
`;

const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: 12px;
  text-align: left;
`;

const PositionTitle = styled.p``;

const PositionDetail = styled.p`
  font-size: 20px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.3px;
  color: ${chartColor.graphMobile};
`;

const ChartWrapper = styled.div`
  width: 800px;
  height: 170px;
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
`;
